import type { Trip } from '@/services/trips';
import type { Booking } from '@/services/bookings';
import type { ItineraryItem } from '@/services/itinerary';
import { tripHasSavedLodging, tripHasSavedTransport } from '@/utils/tripLogisticsReadiness';
import type { TripBudgetSnapshot } from '@/types/budget';
import { generateBudgetAlertForTrip } from '@/utils/budgetAlerts';
import { checkVisaRequirement } from '@/utils/visaCheck';
import { isTripCompleted } from '@/utils/tripStatus';
import type { TravelRequirements } from '@/services/travelRequirements';

export type TripAlert = {
  id: string;
  type:
    | 'visa'
    | 'collaborators'
    | 'docs'
    | 'check_in'
    | 'budget'
    | 'missing_transport'
    | 'missing_lodging'
    | 'other';
  title: string;
  message: string;
  tripId: string;
  tripName: string;
  actionLabel?: string;
  onAction?: () => void;
  priority: 'high' | 'medium' | 'low';
  budgetMetadata?: {
    thresholdPercent: number;
    spentAmount: number;
    budgetAmount: number;
    spentFormatted: string;
    budgetFormatted: string;
    isOverBudget: boolean;
  };
};

/**
 * Check if trip has collaborators
 */
export function hasCollaborators(trip: Trip): boolean {
  return (trip.collaborators?.length ?? 0) > 0;
}

/**
 * Check if trip is starting soon (within 7 days)
 */
function isStartingSoon(trip: Trip): boolean {
  const now = new Date();
  const daysUntil = Math.ceil((trip.startDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return daysUntil >= 0 && daysUntil <= 7;
}

/**
 * Check if trip is in progress (started but not ended)
 */
function isInProgress(trip: Trip): boolean {
  const now = new Date();
  return now >= trip.startDate && now <= trip.endDate;
}

function isPreTripReadinessEligible(trip: Trip): boolean {
  const now = new Date();
  if (isTripCompleted(trip)) {
    return false;
  }
  return now < trip.startDate;
}

/**
 * Generate alerts for a single trip
 */
export function generateTripAlerts(
  trip: Trip,
  userId: string,
  budgetSnapshot?: TripBudgetSnapshot,
  options?: {
    bookings?: Booking[];
    itineraryItems?: ItineraryItem[];
    citizenship?: string;
    travelRequirementsByTripId?: Record<string, TravelRequirements | null>;
  }
): TripAlert[] {
  const alerts: TripAlert[] = [];
  const isOwner = trip.owner === userId;
  const daysUntil = Math.ceil((trip.startDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

  // Only show alerts for active (non-archived) trips
  if (trip.isArchived) {
    return alerts;
  }

  if (isTripCompleted(trip)) {
    return alerts;
  }

  // Visa alert - prefer TravelRequirements (cached) when available
  const travelRequirements = options?.travelRequirementsByTripId?.[trip.id] ?? null;
  if (isOwner && travelRequirements) {
    if (travelRequirements.visaRequired) {
      const priority = isStartingSoon(trip) ? 'high' : 'medium';
      const title =
        travelRequirements.visaStatus === 'visa_required'
          ? 'Visa required'
          : 'Check visa requirements';
      alerts.push({
        id: `visa-${trip.id}`,
        type: 'visa',
        title,
        message:
          travelRequirements.visaNotes ||
          `Your trip to ${trip.destination || 'destination'} may require a visa. Make sure to check visa requirements and apply if needed.`,
        tripId: trip.id,
        tripName: trip.name,
        actionLabel: 'Check visa requirements',
        priority,
      });
    }
  } else if (isOwner && options?.citizenship) {
    // Fallback to smart visa check that considers citizenship and country (not just city)
    const visaResult = checkVisaRequirement(trip.destination, options?.citizenship, trip.travelingFrom);
    if (visaResult.status !== 'not_required') {
      const priority = isStartingSoon(trip) ? 'high' : 'medium';
      const title = visaResult.status === 'required' ? 'Visa required' : 'Check visa requirements';
      alerts.push({
        id: `visa-${trip.id}`,
        type: 'visa',
        title,
        message: visaResult.message || `Your trip to ${trip.destination || 'destination'} may require a visa. Make sure to check visa requirements and apply if needed.`,
        tripId: trip.id,
        tripName: trip.name,
        actionLabel: 'Check visa requirements',
        priority,
      });
    }
  }

  // Collaborators alert - medium priority
  if (isOwner && !hasCollaborators(trip)) {
    alerts.push({
      id: `collaborators-${trip.id}`,
      type: 'collaborators',
      title: 'Add collaborators',
      message: 'Invite travel companions to collaborate on this trip. They can help plan and share expenses.',
      tripId: trip.id,
      tripName: trip.name,
      actionLabel: 'Add collaborators',
      priority: 'medium',
    });
  }

  // Documents alert - only show if trip is starting within 30 days
  // This prevents showing alerts for trips too far in the future
  if (isOwner && daysUntil >= 0 && daysUntil <= 30) {
    const priority = isStartingSoon(trip) ? 'high' : 'medium';
    alerts.push({
      id: `docs-${trip.id}`,
      type: 'docs',
      title: 'Add travel documents',
      message: 'Upload important documents like passports, visas, travel insurance, and booking confirmations.',
      tripId: trip.id,
      tripName: trip.name,
      actionLabel: 'Go to documents',
      priority,
    });
  }

  // Check-in alert - high priority if trip is starting within 24 hours
  if (daysUntil >= 0 && daysUntil <= 1 && !isInProgress(trip)) {
    alerts.push({
      id: `checkin-${trip.id}`,
      type: 'check_in',
      title: 'Check-in reminder',
      message: daysUntil === 0
        ? `Your trip "${trip.name}" starts today! Make sure you're ready to go.`
        : `Your trip "${trip.name}" starts tomorrow. Don't forget to check in for flights and accommodations.`,
      tripId: trip.id,
      tripName: trip.name,
      actionLabel: 'View trip details',
      priority: 'high',
    });
  }

  // Budget usage alert
  const budgetAlerts = generateBudgetAlertForTrip(trip, budgetSnapshot);
  alerts.push(...budgetAlerts);

  // Missing transport / lodging (if context provided)
  const bookings = options?.bookings || [];
  const itineraryItems = options?.itineraryItems || [];

  const hasTransport = tripHasSavedTransport(bookings, itineraryItems);
  const hasLodging = tripHasSavedLodging(bookings, itineraryItems);

  const logisticsPriority: TripAlert['priority'] = isStartingSoon(trip)
    ? 'high'
    : daysUntil <= 30
      ? 'medium'
      : 'low';

  if (isPreTripReadinessEligible(trip) && (options?.bookings || options?.itineraryItems)) {
    if (!hasTransport) {
      const tripLabel = trip.destination || trip.name || 'this trip';
      alerts.push({
        id: `missing-transport-${trip.id}`,
        type: 'missing_transport',
        title: 'Add your flight or ride',
        message:
          `We don’t see any transportation for your ${tripLabel} trip. Add your flight, train, or ride so we can keep you on schedule.`,
        tripId: trip.id,
        tripName: trip.name,
        actionLabel: 'Add transport',
        priority: logisticsPriority,
      });
    }

    if (!hasLodging) {
      const tripLabel = trip.destination || trip.name || 'this trip';
      alerts.push({
        id: `missing-lodging-${trip.id}`,
        type: 'missing_lodging',
        title: 'Add where you are staying',
        message:
          `We don’t see any lodging for your ${tripLabel} trip. Add your hotel or stay details to keep everything in one place.`,
        tripId: trip.id,
        tripName: trip.name,
        actionLabel: 'Add lodging',
        priority: logisticsPriority,
      });
    }
  }

  return alerts;
}

/**
 * Generate alerts for multiple trips
 */
export function generateAllTripAlerts(
  trips: Trip[],
  userId: string,
  options?: {
    budgetSnapshots?: Record<string, TripBudgetSnapshot>;
    bookingsByTripId?: Record<string, Booking[]>;
    itineraryByTripId?: Record<string, ItineraryItem[]>;
    citizenship?: string;
    travelRequirementsByTripId?: Record<string, TravelRequirements | null>;
  },
): TripAlert[] {
  const allAlerts: TripAlert[] = [];
  
  trips.forEach((trip) => {
    const alerts = generateTripAlerts(
      trip,
      userId,
      options?.budgetSnapshots?.[trip.id],
      {
        bookings: options?.bookingsByTripId?.[trip.id],
        itineraryItems: options?.itineraryByTripId?.[trip.id],
        citizenship: options?.citizenship,
        travelRequirementsByTripId: options?.travelRequirementsByTripId,
      }
    );
    allAlerts.push(...alerts);
  });

  return allAlerts;
}

